/**
 * A compact, click-to-expand explainer. Collapsed it's just a small "ⓘ label"
 * button, so it costs ~one line of height; expanded it reveals plain-English
 * detail passed as children. Built for the salience pages, where the methodology
 * needs explaining to non-experts without eating screen real-estate.
 *
 * Works on BOTH the dark "terminal" panels (tone="dark", the default) and the
 * light reading chrome (tone="light") — the salience overview header/footer and
 * the methods page render light, the panels render dark.
 *
 * Citations belong INSIDE the expanded body via <Cite>, so a paper reference
 * reads as a supporting footnote rather than the baffling headline it used to be.
 */
import { useState, type ReactNode } from "react";
import { Info, ChevronDown } from "lucide-react";

interface InfoCalloutProps {
  /** Button text in the collapsed state. */
  label?: string;
  tone?: "dark" | "light";
  /** Start expanded (rare — used where the explainer IS the point). */
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

const TONE = {
  dark: {
    button: "text-terminal-ink-muted hover:text-terminal-ink",
    body: "border-white/10 bg-black/20 text-terminal-ink-muted",
  },
  light: {
    button: "text-ink-muted hover:text-brand-indigo",
    body: "border-rule bg-black/3 text-ink-muted",
  },
} as const;

export function InfoCallout({
  label = "How this works",
  tone = "dark",
  defaultOpen = false,
  children,
  className = "",
}: InfoCalloutProps) {
  const [open, setOpen] = useState(defaultOpen);
  const t = TONE[tone];

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1 text-[11px] font-medium transition-colors ${t.button}`}
      >
        <Info size={12} aria-hidden />
        <span>{label}</span>
        <ChevronDown
          size={12}
          aria-hidden
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className={`mt-2 space-y-2 rounded-lg border p-3 text-[12px] leading-relaxed ${t.body}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * A muted citation footnote for use inside an InfoCallout body. Keeps the
 * academic reference present but visually subordinate to the plain-English text.
 */
export function Cite({
  tone = "dark",
  children,
}: {
  tone?: "dark" | "light";
  children: ReactNode;
}) {
  const cls =
    tone === "light" ? "text-ink-muted/70" : "text-terminal-ink-muted/60";
  return (
    <p className={`text-[11px] italic leading-snug ${cls}`}>
      Based on: {children}
    </p>
  );
}
